import { useState, useRef, useEffect, useCallback } from 'react';
import { formatFileSize, formatRelativeTime } from '../utils/helpers';
import { classNames } from '../utils/helpers';
import type { Photo } from '../types';

interface PhotoCardProps {
  photo: Photo;
  index: number;
  isSelected: boolean;
  selectionMode: boolean;
  onClick: () => void;
  onSelect: (id: number) => void;
}

export function PhotoCard({ photo, isSelected, selectionMode, onClick, onSelect }: PhotoCardProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleIntersect = (entries: IntersectionObserverEntry[]) => {
      if (entries[0].isIntersecting && imgRef.current && !isLoaded && !hasError) {
        const img = imgRef.current;
        if (img.complete) {
          setIsLoaded(true);
        } else {
          img.onload = () => setIsLoaded(true);
          img.onerror = () => setHasError(true);
        }
      }
    };

    const observer = new IntersectionObserver(handleIntersect, { rootMargin: '100px' });
    if (cardRef.current) observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, [isLoaded, hasError]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (selectionMode) onSelect(photo.id);
      else onClick();
    }
  }, [selectionMode, onSelect, onClick, photo.id]);

  // Falls kein Thumb existiert (Worker kann nicht überall Thumbs erzeugen),
  // Original lazy laden statt Platzhalter zeigen. Videos nur als <video>, wenn
  // der Browser sie abspielen kann (mp4/webm) — sonst Platzhalter + Download.
  const isVideo = photo.mimeType.startsWith('video/');
  const playableVideo = isVideo && photo.canDisplay;
  const imageUrl = isVideo
    ? (playableVideo ? `/api/photos/${photo.id}` : null)
    : (photo.thumbnailKey
      ? `/api/photos/${photo.id}/thumbnail`
      : (photo.canDisplay ? `/api/photos/${photo.id}` : null));
  const canDisplay = photo.canDisplay;

  return (
    <div
      ref={cardRef}
      className={classNames('photo-card', {
        'photo-card--selected': isSelected,
        'photo-card--selection-mode': selectionMode,
        'photo-card--loaded': isLoaded,
        'photo-card--error': hasError,
        'photo-card--hovered': isHovered,
      })}
      onClick={selectionMode ? () => onSelect(photo.id) : onClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      tabIndex={0}
      role={selectionMode ? 'checkbox' : 'button'}
      aria-checked={isSelected}
      aria-label={selectionMode ? (isSelected ? `Abwählen: ${photo.filename}` : `Auswählen: ${photo.filename}`) : `Anzeigen: ${photo.filename}`}
    >
      <div className="photo-card-image-wrapper">
        {playableVideo && imageUrl ? (
          <video
            src={imageUrl}
            className="photo-card-image"
            preload="metadata"
            muted
            playsInline
          />
        ) : imageUrl ? (
          <img
            ref={imgRef}
            src={imageUrl}
            alt=""
            className="photo-card-image"
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div className="photo-card-placeholder" aria-hidden="true">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
              <circle cx="8.5" cy="8.5" r="1.5"></circle>
              <polyline points="21 15 16 10 5 21"></polyline>
            </svg>
            <span className="photo-card-placeholder-text">
              {canDisplay ? 'Vorschau lädt…' : 'Originaldatei herunterladen'}
            </span>
          </div>
        )}

        {selectionMode && (
          <div className="photo-card-selection">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={(e) => {
                e.stopPropagation();
                onSelect(photo.id);
              }}
              aria-label={`Auswählen: ${photo.filename}`}
            />
          </div>
        )}

        {!canDisplay && (
          <div className="photo-card-format-badge" title={`${photo.mimeType} &middot; ${formatFileSize(photo.fileSize)}`}>
            {photo.mimeType.split('/')[1]?.toUpperCase() || 'FILE'}
          </div>
        )}

        {!isLoaded && !hasError && (
          <div className="photo-card-loading" aria-hidden="true">
            <div className="spinner spinner-sm"></div>
          </div>
        )}
      </div>

      <div className="photo-card-info">
        <div className="photo-card-filename" title={photo.filename}>
          {photo.filename}
        </div>
        <div className="photo-card-meta">
          {photo.displayName && (
            <span className="photo-card-uploader" title={photo.displayName}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
              {photo.displayName}
            </span>
          )}
          <time className="photo-card-date" dateTime={new Date(photo.uploadedAt).toISOString()}>
            {formatRelativeTime(photo.uploadedAt)}
          </time>
        </div>
        <div className="photo-card-size">
          {formatFileSize(photo.fileSize)}
          {photo.width && photo.height && ` &middot; ${photo.width}×${photo.height}`}
        </div>
      </div>
    </div>
  );
}